"use client";

import * as React from "react";
import Image from "next/image";
import RichText from "@/components/RichText";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import { CMSLink } from "@/Link";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import type { News, Page } from "@/payload-types";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";
import { mediaObjectFitClassName } from "@/utilities/mediaObjectFit";

export type GallerySection11Link = {
	type?: "custom" | "reference" | null;
	newTab?: boolean | null;
	reference?: {
		relationTo: "pages" | "news";
		value: Page | News | string | number;
	} | null;
	url?: string | null;
	label?: string | null;
	appearance?: "default" | "outline" | "underline" | null;
};

export type GallerySection11Slide = {
	title?: string;
	body?: string | DefaultTypedEditorState;
	links?: GallerySection11Link[];
	imageAlt?: string;
	imageTitle?: string;
	imageSrc?: string;
	imageObjectFit?: MediaObjectFit;
	backgroundImageTitle?: string;
	backgroundImageSrc?: string;
	backgroundImageObjectFit?: MediaObjectFit;
};

export function GallerySection11({
	tagline,
	title,
	body,
	titleTag = "h2",
	titleClassName = "heading-lg",
	sectionBackgroundImageSrc,
	sectionBackgroundImageAlt,
	sectionBackgroundImageTitle,
	sectionBackgroundImageObjectFit = "cover",
	links,
	slides = [],
}: {
	tagline?: string;
	title?: string;
	body?: string;
	titleTag?: "h1" | "h2" | "h3";
	titleClassName?: string;
	sectionBackgroundImageSrc?: string;
	sectionBackgroundImageAlt?: string;
	sectionBackgroundImageTitle?: string;
	sectionBackgroundImageObjectFit?: MediaObjectFit;
	links?: GallerySection11Link[];
	slides?: GallerySection11Slide[];
}) {
	const TitleTag = titleTag;
	const [api, setApi] = React.useState<CarouselApi>();
	const [current, setCurrent] = React.useState(0);
	const [count, setCount] = React.useState(0);

	React.useEffect(() => {
		if (!api) {
			return;
		}

		setCount(api.scrollSnapList().length);
		setCurrent(api.selectedScrollSnap() + 1);

		api.on("select", () => {
			setCurrent(api.selectedScrollSnap() + 1);
		});
	}, [api]);

	if (!slides.length) return null;

	return (
		<section className="bg-background section-padding-y">
			<div className="container-padding-x container mx-auto">
				<div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
					<div className="section-title-gap-lg flex flex-col items-start text-left lg:col-span-5 lg:items-start">
						{tagline ? <Tagline>{tagline}</Tagline> : null}
						<div className="flex flex-col gap-2 pb-6:last:last pr-4">
							{title ? (
								<TitleTag className={`${titleClassName} text-foreground`}>
									{title}
								</TitleTag>
							) : null}
							{body ? (
								<p className={`${titleClassName} text-muted-foreground`}>
									{body}
								</p>
							) : null}
						</div>
						{links?.length ? (
							<div className="flex flex-row flex-wrap gap-3">
								{links.map((link, index) => (
									<CMSLink
										key={`${link.label ?? "link"}-${index}`}
										{...link}
										appearance={link.appearance ?? "underline"}
										size="lg"
										className="group"
									>
										<ArrowRight className="transition-transform duration-200 group-hover:translate-x-1 group-hover/button:translate-x-1" />
									</CMSLink>
								))}
							</div>
						) : null}
					</div>
					<Carousel
						setApi={setApi}
						opts={{
							align: "start",
							loop: true,
						}}
						className="relative mb-12 w-full lg:col-span-7"
					>
						{sectionBackgroundImageSrc ? (
							<div className="pointer-events-none absolute inset-0 hidden overflow-hidden rounded-sm lg:block lg:rounded-xs">
								<Image
									src={sectionBackgroundImageSrc}
									alt={sectionBackgroundImageAlt ?? ""}
									title={
										sectionBackgroundImageTitle ??
										sectionBackgroundImageAlt ??
										undefined
									}
									fill
									aria-hidden={sectionBackgroundImageAlt ? undefined : true}
									className={mediaObjectFitClassName(
										sectionBackgroundImageObjectFit,
									)}
								/>
							</div>
						) : null}
						<div className="mb-4 flex items-center gap-2 lg:hidden">
							<CarouselPrevious className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
							<div className="text-foreground flex min-w-14 items-center justify-center text-sm font-medium">
								{current} / {count}
							</div>
							<CarouselNext className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
						</div>
						<CarouselContent className="items-stretch">
							{slides.map((slide, index) => {
								const selectedSnapIndex = current - 1;
								const indexOfSlideToMakeDim =
									count > 0 ? (selectedSnapIndex + 1) % count : -1;

								return (
									<CarouselItem
										key={index}
										className={cn(
											"flex h-auto self-stretch",
											"first:pl-4 pl-4",
											sectionBackgroundImageSrc &&
												slides[index].imageSrc &&
												slides.length < 3
												? "last:pr-6"
												: "",
											sectionBackgroundImageSrc ? "" : "lg:first:pl-6 lg:pl-6",
											"basis-9/10",
											index === indexOfSlideToMakeDim && count > 1
												? "opacity-80"
												: "",
										)}
									>
										<div
											className={cn(
												"relative w-full h-full overflow-hidden rounded-sm lg:rounded-xs lg:pl-6 lg:py-6",
											)}
										>
											<div className="relative z-10 flex h-full flex-col rounded-sm bg-[#f1f0ee]">
												<div className="relative aspect-3/2 overflow-hidden">
													<div
														className={cn(
															"relative h-full w-full overflow-hidden lg:rounded-t-xs",
														)}
													>
														{slide.backgroundImageSrc ? (
															<Image
																src={slide.backgroundImageSrc}
																alt=""
																fill
																aria-hidden
																className={cn(
																	"h-full w-full",
																	mediaObjectFitClassName(
																		slide.backgroundImageObjectFit,
																	),
																)}
															/>
														) : null}
														{slide.imageSrc ? (
															<div
																className={cn(
																	"relative z-10 h-full w-full p-6",
																)}
															>
																<div
																	className={cn(
																		"relative h-full w-full overflow-hidden lg:rounded-xs",
																	)}
																>
																	<Image
																		src={slide.imageSrc}
																		alt={slide.imageAlt ?? ""}
																		title={
																			slide.imageTitle ??
																			slide.imageAlt ??
																			undefined
																		}
																		fill
																		className={cn(
																			"h-full w-full max-lg:rounded-t-sm max-lg:rounded-b-none lg:rounded-xs",
																			mediaObjectFitClassName(
																				slide.imageObjectFit,
																			),
																		)}
																	/>
																</div>
															</div>
														) : null}
													</div>
												</div>
												<div className="flex flex-1 flex-col gap-8 pt-8 p-6">
													<div className="flex flex-col">
														<div className="flex flex-col gap-2 pb-6:last:last">
															<h3 className="text-xl text-foreground text-pretty">
																{slide.title}
															</h3>
															{typeof slide.body === "string" ? (
																<p className="text-muted-foreground text-xl text-pretty">
																	{slide.body}
																</p>
															) : slide.body ? (
																<RichText
																	data={slide.body}
																	enableGutter={false}
																	enableProse={false}
																	className="text-muted-foreground text-xl text-pretty"
																/>
															) : null}
														</div>
													</div>

													{slide.links?.length ? (
														<div className="mt-auto flex h-full flex-col items-start justify-end gap-3">
															{slide.links.map((link, index) => (
																<CMSLink
																	key={`${link.label ?? "link"}-${index}`}
																	{...link}
																	appearance={link.appearance ?? "underline"}
																	size="lg"
																	className="group"
																>
																	<ArrowRight className="transition-transform duration-200 group-hover:translate-x-1 group-hover/button:translate-x-1" />
																</CMSLink>
															))}
														</div>
													) : null}
												</div>
											</div>
										</div>
									</CarouselItem>
								);
							})}
						</CarouselContent>
						<div className="absolute right-0 -top-16 hidden gap-2 lg:flex">
							<CarouselPrevious className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
							<div className="text-foreground flex min-w-14 items-center justify-center text-sm font-medium">
								{current} / {count}
							</div>
							<CarouselNext className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
						</div>
						<div className="absolute right-0 -bottom-12 left-0 py-4 md:right-auto md:-bottom-16">
							<div className="flex items-center justify-center gap-2">
								{Array.from({ length: count }).map((_, index) => (
									<button
										key={index}
										onClick={() => api?.scrollTo(index)}
										className={cn(
											"h-2 w-2 rounded-full",
											current === index + 1 ? "bg-primary" : "bg-muted",
										)}
										aria-label={`Go to slide ${index + 1}`}
									/>
								))}
							</div>
						</div>
					</Carousel>
				</div>
			</div>
		</section>
	);
}
